import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, Award, Calendar, Video, User, Package, HelpCircle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import Recommendations from '../components/Recommendations';

// Standalone 1-day online training — Catering & Operations Management
// Distinct from the cohort program. Single dedicated landing page.
const TRAINING_APPLICATION_LINK = '/program-application/catering-operations';
const DISCOVERY_CALL_URL = 'https://calendly.com/nicolas-gate33/discovery-call';
const PRICE_EUR = '€490';

const UPCOMING_COHORTS = [
  { date: '21 September 2026', status: 'Open' },
  { date: '27 September 2026', status: 'Open' },
  { date: '19 October 2026', status: 'Open' },
  { date: '26 October 2026', status: 'Open' },
  { date: '4 November 2026', status: 'Open' },
  { date: '9 November 2026', status: 'Open' },
  { date: '16 December 2026', status: 'Open' },
];

const DELIVERABLES = [
  {
    id: 'template',
    title: 'Catering order template',
    body: 'Organised by flight-time bracket — ultra-short to ultra-long — so you never start from a blank page.',
  },
  {
    id: 'suppliers',
    title: 'Suppliers &amp; hotels by region',
    body: 'Reference list of catering companies and hotels by region for when your primary supplier falls through.',
  },
  {
    id: 'card',
    title: 'Quick-reference help card',
    body: 'IATA meal codes, Atlas box sizing, and the +20% rule — the essentials on a single sheet.',
  },
  {
    id: 'community',
    title: 'Gate 33 WhatsApp community',
    body: 'Ongoing access to the private group — real cabin debriefs, operator listings, recruiter conversations.',
  },
];

const FAQ = [
  {
    q: 'Do I need private aviation experience to join?',
    a: 'No. This training assumes commercial or hospitality experience and builds the private-aviation-specific layer on top of it.',
  },
  {
    q: "What if I can't attend the full live day?",
    a: 'The session is live and not recorded, and cohorts are capped, so please commit to the full day before booking. Contact us before your cohort date if you need to discuss rescheduling.',
  },
  {
    q: 'Is this the same as the 3-Day Academy?',
    a: 'No. This is a standalone, focused day on catering intelligence and cabin operations logistics — ordering, packaging, FBO coordination, and recovery. It does not cover plating, service technique, cultural protocol, CV positioning, or interview prep, which belong to the 3-Day Academy.',
  },
  {
    q: 'What do I need to attend?',
    a: 'A laptop with camera and mic, a stable connection, and a quiet space for the day.',
  },
];

const outcomes = [
  { id: 'cycle', text: 'Manage the entire catering and operations cycle of a private flight independently — from brief to boarding.' },
  { id: 'judgement', text: 'Apply the professional judgement that separates good crew from exceptional crew.' },
  { id: 'aircraft', text: 'Handle any aircraft, any destination, and any client profile with confidence.' },
  { id: 'logic', text: 'Understand the logic behind every catering decision — not just the what, but the why.' },
];

const curriculum = [
  {
    n: '01',
    title: 'Reading and interpreting the flight brief',
    body: 'Decode a flight brief and extract everything that matters for catering and service planning.',
  },
  {
    n: '02',
    title: 'Building the passenger profile',
    body: 'Translate passenger preferences, dietary requirements, allergies, and cultural or religious considerations into concrete catering choices.',
  },
  {
    n: '03',
    title: 'Knowing your aircraft',
    body: 'Understand how the specific aircraft shapes every decision: galley capabilities, storage, and onboard constraints.',
  },
  {
    n: '04',
    title: 'Drafting the menu',
    body: 'Build a coherent, client-appropriate menu that fits the flight profile, duration, time of day, and passenger expectations.',
  },
  {
    n: '05',
    title: 'The logic behind catering orders',
    body: 'Why orders are structured the way they are — quantities, timing, backups, and anticipating last-minute changes.',
  },
  {
    n: '06',
    title: 'Onboard equipment & packaging',
    body: 'Identify what equipment is available on board and choose the right packaging so food travels, stores, and presents perfectly.',
  },
  {
    n: '07',
    title: 'Understanding the departure location',
    body: 'How the location you order from changes your options, your timing, and your suppliers.',
  },
  {
    n: '08',
    title: 'Placing orders — guests and crew',
    body: 'Master ordering by location: a direct order from a restaurant, or through specialist providers such as DeliSky or Private Flight Catering — and know when to use which.',
  },
  {
    n: '09',
    title: 'Liaising with FBO and airport stakeholders',
    body: 'Coordinate effectively with the FBO and the different airport actors to ensure smooth delivery and handling.',
  },
  {
    n: '10',
    title: 'Receiving and organising onboard',
    body: 'Receive the order, check it, and re-organise everything on board for a flawless, ready-to-serve cabin.',
  },
];

const FaqAccordion = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
      {FAQ.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={item.q} data-testid={`catering-faq-item-${i}`}>
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              data-testid={`catering-faq-toggle-${i}`}
              className="w-full flex items-start justify-between gap-6 py-6 text-left group"
            >
              <span className="font-heading text-lg text-[#0F0F0F] leading-snug">
                {item.q}
              </span>
              <ChevronDown
                size={20}
                className={`text-[#C5A880] flex-shrink-0 mt-1 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                strokeWidth={1.6}
              />
            </button>
            {isOpen && (
              <p
                data-testid={`catering-faq-answer-${i}`}
                className="text-[#5A5A5A] leading-relaxed pb-6 pr-8"
              >
                {item.a}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CateringOperationsTrainingPage = () => (
  <div data-testid="catering-operations-training-page" className="min-h-screen">
    {/* Redirect banner — points to the flagship 3-Day Online Training */}
    <div data-testid="catering-flagship-banner" className="bg-[#0F0F0F] text-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center justify-center gap-3 text-center">
        <span className="text-xs uppercase tracking-[0.18em] text-[#C5A880]">Looking for the complete program?</span>
        <Link
          to="/academy"
          data-testid="catering-flagship-banner-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#FDFBF7] hover:text-[#C5A880] transition-colors"
        >
          3-Day Online Training <ArrowRight size={12} />
        </Link>
      </div>
    </div>
    {/* Hero */}
    <section
      data-testid="catering-training-hero"
      className="relative min-h-[70vh] flex items-center"
    >
      <div className="absolute inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/6mdpskxv_ChatGPT%20Image%20May%2028%2C%202026%20at%2011_45_49%20AM.png"
          alt="Gate 33 private aviation training and consulting — Catering & Operations cabin attendants"
          title="Gate 33 private aviation training and consulting — Catering & Operations cabin attendants"
          className="w-full h-full object-cover object-[center_25%]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/75 to-[#FDFBF7]/15" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="max-w-2xl">
          <span className="overline inline-block mb-6">Standalone Online Training</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
            Catering &amp; Operations Training
          </h1>
          <p className="text-lg text-[#5A5A5A] leading-relaxed mb-4">
            One day. Fully online. The operational backbone of every private flight — from flight-prep through catering ordering — taught the way premium operators actually run it.
          </p>
          <p className="text-sm uppercase tracking-[0.18em] text-[#C5A880] font-semibold mb-4">
            Live real-time training with Nicolas — not a recorded course.
          </p>
          <p className="text-base text-[#1A1A1A] font-medium mb-10">
            For commercial cabin crew, hospitality professionals, and freelance cabin attendants who need the operational layer no commercial training ever taught them.
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="catering-hero-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Book a Discovery Call <ArrowRight size={16} />
            </a>
            <div className="flex items-center gap-3 text-sm text-[#1A1A1A]">
              <span className="text-2xl font-heading text-[#0F0F0F]">{PRICE_EUR}</span>
              <span className="text-[#5A5A5A]">· 1 day · Live online</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Trainer bio blurb */}
    <section data-testid="catering-trainer-bio" className="bg-[#FDFBF7] border-b border-[#E5E0D8] py-10 md:py-12">
      <div className="container-custom max-w-4xl">
        <div className="flex items-start gap-5 md:gap-6">
          <div className="w-11 h-11 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center border border-[#C5A880]">
            <User size={18} className="text-[#C5A880]" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A] font-semibold mb-2">
              Your trainer · Nicolas Perot dos Santos
            </div>
            <p className="text-[#5A5A5A] leading-relaxed">
              Years at Qatar Airways and Etihad Airways before transitioning into
              business aviation, flying UHNW clients on premium aircraft. It took 120
              applications to break into the industry. Gate 33 exists so the next
              candidate doesn't need as many.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Course Overview — Editorial 2-column layout */}
    <section data-testid="catering-training-overview" className="section-padding bg-white">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* LEFT — editorial copy */}
          <div>
            <span className="overline inline-block mb-4">Course overview</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-8">
              From flight brief to ready-to-serve cabin.
            </h2>
            <p className="text-[#1A1A1A] text-lg leading-relaxed mb-6 font-medium">
              The complete operational journey of a private flight's catering — in one day.
            </p>
            <p className="text-[#5A5A5A] leading-relaxed mb-6">
              This intensive one-day online course takes you through every step of the cycle, from the moment you receive the flight brief to the moment everything is organised and ready on board.
            </p>
            <p className="text-[#5A5A5A] leading-relaxed">
              You won't just learn what to order. You'll understand the logic behind every decision — so you can handle any aircraft, any destination, and any client profile with confidence.
            </p>
          </div>

          {/* RIGHT — cream editorial card with figure */}
          <div className="relative">
            <div className="bg-[#F5F3EF] aspect-[5/6] flex flex-col items-center justify-center text-center px-10">
              <div
                className="font-heading text-[#C5A880]/30 leading-none mb-2"
                style={{ fontSize: 'clamp(120px, 16vw, 200px)' }}
              >
                10
              </div>
              <div className="text-[#5A5A5A] text-base tracking-wide -mt-3">
                Operational steps
              </div>
              <div className="mt-6 text-xs uppercase tracking-[0.22em] text-[#1A1A1A]">
                1 day · live online · {PRICE_EUR}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Recommendations — editorial auto-fade carousel */}
    <section data-testid="catering-training-recommendations" className="bg-white pb-20">
      <Recommendations
        inline
        centered
        compact
        variant="light"
        eyebrow="Industry Perspective"
        title="The Reality Behind The Transition"
        testId="catering-recommendations"
      />
    </section>

    {/* What you walk away with */}
    <section data-testid="catering-training-outcomes" className="section-padding bg-[#FDFBF7]">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <span className="overline inline-block mb-4">What you walk away with</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              An operational foundation you can deploy on the very next flight.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              By the end of the day, you'll be able to manage the entire catering and operations cycle of a private flight independently — with the professional judgement that separates good crew from exceptional crew.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {outcomes.map((o) => (
                <li key={o.id} className="flex items-start gap-4 p-5 bg-white border border-[#E5E0D8]">
                  <CheckCircle2 size={20} className="text-[#C5A880] flex-shrink-0 mt-1" />
                  <span className="text-[#1A1A1A] leading-relaxed">{o.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* Curriculum — 10 steps editorial grid */}
    <section data-testid="catering-training-curriculum" className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="overline inline-block mb-4">What you'll learn — step by step</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
            Ten steps. One operational day.
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed">
            Structured around the real-world sequence of a private flight — every step compounds on the previous one, building the full mental model of catering operations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 max-w-5xl mx-auto">
          {curriculum.map((step) => (
            <div
              key={step.n}
              data-testid={`catering-curriculum-${step.n}`}
              className="flex items-start gap-6 border-l-2 border-[#C5A880] pl-6 py-1"
            >
              <span className="font-heading text-3xl text-[#C5A880] flex-shrink-0 leading-none">
                {step.n}
              </span>
              <div>
                <h3 className="font-heading text-lg mb-2 text-[#0F0F0F] leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#5A5A5A] text-sm leading-relaxed">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Format & Price */}
    <section data-testid="catering-training-format" className="section-padding bg-[#FDFBF7]">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="overline inline-block mb-4">Format</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
              Live online. Small cohorts. Direct execution.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed mb-8">
              The training is delivered over one full day in a live cohort format. You work through three structured modules with hands-on exercises, anchored by real cases from current operations. Cohorts are deliberately small to keep the trainer's attention on each candidate's output.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-5 bg-[#F5F3EF]">
                <Clock size={22} className="text-[#C5A880] mb-3" />
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Duration</h4>
                <p className="text-sm text-[#5A5A5A]">1 day · 7 hours</p>
              </div>
              <div className="p-5 bg-[#F5F3EF]">
                <Video size={22} className="text-[#C5A880] mb-3" />
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Format</h4>
                <p className="text-sm text-[#5A5A5A]">Live online cohort</p>
              </div>
              <div className="p-5 bg-[#F5F3EF]">
                <Calendar size={22} className="text-[#C5A880] mb-3" />
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Cohorts</h4>
                <p className="text-sm text-[#5A5A5A]">Monthly · capped seats</p>
              </div>
              <div className="p-5 bg-[#F5F3EF]">
                <Award size={22} className="text-[#C5A880] mb-3" />
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Outcome</h4>
                <p className="text-sm text-[#5A5A5A]">Gate 33 attestation</p>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 lg:p-12">
            <div className="text-center">
              <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-6">
                Training Investment
              </span>
              <div className="mb-2">
                <span className="font-heading text-6xl text-[#FDFBF7]">{PRICE_EUR}</span>
              </div>
              <p className="text-[#9A9A9A] text-sm mb-8">VAT included · one-time payment per cohort.</p>
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="catering-format-cta"
                className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300 w-full justify-center"
              >
                Book a Discovery Call
                <ArrowRight size={16} />
              </a>
              <p className="text-[#6A6A6A] text-xs mt-6">Seats are capped per cohort.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Upcoming cohorts */}
    <section data-testid="catering-training-schedule" className="section-padding bg-white">
      <div className="container-custom max-w-4xl">
        <div className="text-center mb-12">
          <span className="overline inline-block mb-4">Schedule</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-4">
            Upcoming live cohorts
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed max-w-2xl mx-auto">
            Each date runs the day before the corresponding Gate 33 Academy cohort, live online, 7 hours.
          </p>
        </div>
        <ul className="divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
          {UPCOMING_COHORTS.map((c) => (
            <li
              key={c.date}
              data-testid={`catering-cohort-${c.date.replace(/\s+/g, '-')}`}
              className="grid grid-cols-[auto_1fr_auto] gap-4 items-center py-5"
            >
              <Calendar size={16} className="text-[#C5A880]" strokeWidth={1.6} />
              <span className="font-heading text-lg text-[#0F0F0F]">{c.date}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#0F0F0F] border border-[#C5A880] bg-[#FDFBF7] px-3 py-1 font-semibold">
                {c.status}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* Deliverables — What you leave with */}
    <section data-testid="catering-training-deliverables" className="section-padding bg-[#FDFBF7]">
      <div className="container-custom max-w-5xl">
        <div className="text-center mb-14">
          <span className="overline inline-block mb-4">What you leave with</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
            Tools, references, and ongoing community.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {DELIVERABLES.map((d) => (
            <div
              key={d.id}
              data-testid={`catering-deliverable-${d.id}`}
              className="bg-white border border-[#E5E0D8] p-8 flex gap-5 items-start"
            >
              <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-[#C5A880]">
                <Package size={18} className="text-[#C5A880]" strokeWidth={1.6} />
              </div>
              <div>
                <h3 className="font-heading text-lg text-[#0F0F0F] mb-2" dangerouslySetInnerHTML={{ __html: d.title }} />
                <p className="text-sm text-[#5A5A5A] leading-relaxed">{d.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Who it's for */}
    <section data-testid="catering-training-audience" className="section-padding bg-white">
      <div className="container-custom max-w-4xl">
        <div className="text-center mb-12">
          <span className="overline inline-block mb-4">Who it's for</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
            For crew who want to master the full catering &amp; ops workflow.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <h4 className="font-heading text-lg mb-3">VIP flight attendants</h4>
            <p className="text-[#5A5A5A] text-sm leading-relaxed">
              Already flying private and looking to refine the operational discipline expected by premium operators across the cycle — brief to boarding.
            </p>
          </div>
          <div className="text-center">
            <h4 className="font-heading text-lg mb-3">Cabin crew transitioning to private</h4>
            <p className="text-[#5A5A5A] text-sm leading-relaxed">
              Commercial cabin crew preparing the move, who need the autonomous operational layer no airline training ever covers.
            </p>
          </div>
          <div className="text-center">
            <h4 className="font-heading text-lg mb-3">Anyone serious about the role</h4>
            <p className="text-[#5A5A5A] text-sm leading-relaxed">
              Hospitality professionals, freelance crew, or career changers who want the full catering &amp; ops workflow of a private flight, taught the way it's actually run.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section data-testid="catering-training-faq" className="section-padding bg-white">
      <div className="container-custom max-w-3xl">
        <div className="mb-10 text-center">
          <span className="overline inline-block mb-4">Frequently Asked</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight">
            The questions candidates actually ask.
          </h2>
        </div>
        <FaqAccordion />
      </div>
    </section>

    {/* Bundle — Add Mentorship */}
    <section data-testid="catering-training-bundle" className="section-padding bg-[#FDFBF7]">
      <div className="container-custom max-w-4xl">
        <div className="border border-[#E5E0D8] p-10 md:p-14 bg-white">
          <span className="overline inline-block mb-4">After the training</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
            Want ongoing support after this training?
          </h2>
          <p className="text-[#5A5A5A] leading-relaxed mb-8">
            Continue with structured mentorship: 1:1 calls, CV & LinkedIn work, curated operator access, and mock interviews. Two formats — Light (6–8 weeks) or Full (3 months).
          </p>
          <p className="text-[#1A1A1A] leading-relaxed mb-8 font-medium">
            Ask about adding Mentorship during your discovery call.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/mentorship"
              data-testid="catering-bundle-mentorship-link"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Learn about Mentorship <ArrowRight size={14} />
            </Link>
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="catering-bundle-discovery-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Book a Discovery Call <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Final CTA */}
    <section data-testid="catering-training-final-cta" className="section-dark py-12 md:py-16 border-b border-[#C5A880]/40">
      <div className="container-custom text-center">
        <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
          Next cohort fills quickly
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
          Close the operational gap.
        </h2>
        <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
          One day. {PRICE_EUR}. Live online with Nicolas. The operational discipline premium operators actually hire for.
        </p>
        <a
          href={DISCOVERY_CALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="catering-final-cta"
          className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
        >
          Book a Discovery Call
          <ArrowRight size={16} />
        </a>
      </div>
    </section>
  </div>
);

export default CateringOperationsTrainingPage;
